import URL from 'url';

import React, { PureComponent, ReactNode } from 'react';

import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import { token } from '@atlaskit/tokens';

import { TreeSubmodule } from '../types';

import {
  LinkInnerWrapper,
  LinkIconContainer,
  LinkTextContainer,
} from './directory.style';
import * as styles from './submodule-link.style';

type SubmoduleLinkProps = {
  entry: TreeSubmodule;
  renderName: (name: string) => ReactNode;
  icon?: ReactNode;
};

type URLParts = {
  host: string;
  path: string;
};

type SubmoduleLinkData = {
  commit: string;
  source: string;
  isExternal: boolean;
  repoName: string;
};

function parseSSHShortURL(url: string, bbHost: string): URLParts | null {
  const sshShortPattern = new RegExp(`.+@(github.com|${bbHost}):(.*)([.]git)?`);
  /* eslint-disable no-sparse-arrays */
  // prettier-ignore
  const [, host, path] = sshShortPattern.exec(url) || [, , , ];
  /* eslint-enable no-sparse-arrays */

  if (!host || !path) {
    return null;
  }

  return { host, path: `/${path}` };
}

function parseRFC3986URL(url: string): URLParts | null {
  const urlObj = URL.parse(url);
  const { host, pathname: path } = urlObj;

  if (!host || !path) {
    return null;
  }

  return { host, path };
}

export function getURLs(
  url: string | null | undefined,
  hash: string,
  bbHost: string
): SubmoduleLinkData | null {
  if (!url) {
    return null;
  }

  const urlParts = parseSSHShortURL(url, bbHost) || parseRFC3986URL(url);

  if (!urlParts) {
    return null;
  }

  const { host } = urlParts;
  let { path } = urlParts;

  if (path && path.endsWith('.git')) {
    path = path.substr(0, path.length - 4);
  }

  const pathParts = path.split('/');
  const repoName = pathParts[pathParts.length - 1];

  switch (host) {
    case bbHost:
      return {
        source: `${path}/src/${hash}/`,
        commit: `${path}/commits/${hash}/`,
        isExternal: false,
        repoName,
      };
    case 'github.com':
      return {
        source: `https://${host}${path}/tree/${hash}/`,
        commit: `https://${host}${path}/commit/${hash}/`,
        isExternal: true,
        repoName,
      };
    default:
      return null;
  }
}

export default class SubmoduleLink extends PureComponent<SubmoduleLinkProps> {
  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    renderName: (name: string) => name,
  };

  renderLinkContent = (icon: React.ReactNode, text: ReactNode) => (
    <LinkInnerWrapper>
      {icon && <LinkIconContainer>{icon}</LinkIconContainer>}
      <LinkTextContainer>{text}</LinkTextContainer>
    </LinkInnerWrapper>
  );

  render(): ReactNode {
    const { entry, renderName, icon } = this.props;

    const renderedName = renderName(this.props.entry.name);
    const url = getURLs(entry.url, entry.hash, window.location.host);
    const shortHash = entry.hash.substr(0, 12);

    if (!url) {
      return (
        <div>
          {renderedName}
          &nbsp;[
          {shortHash}]
        </div>
      );
    }

    const arrowIndicator =
      url.repoName !== entry.name ? (
        <styles.ArrowIndicator>
          <ArrowRightIcon
            size="medium"
            primaryColor={token('color.icon')}
            label=""
          />{' '}
          <span>{url.repoName}</span>
        </styles.ArrowIndicator>
      ) : null;

    if (url.isExternal) {
      return (
        <styles.SubmoduleLink>
          <a href={url.source} rel="nofollow">
            {this.renderLinkContent(icon, renderedName)}
          </a>
          {arrowIndicator}
          &nbsp;[
          <a href={url.commit} rel="nofollow">
            {shortHash}
          </a>
          ]{' '}
        </styles.SubmoduleLink>
      );
    }

    return (
      <styles.SubmoduleLink>
        <a href={url.source} rel="nofollow">
          {this.renderLinkContent(icon, renderedName)}
        </a>
        {arrowIndicator}
        &nbsp;[
        <a href={url.commit} rel="nofollow">
          {shortHash}
        </a>
        ]
      </styles.SubmoduleLink>
    );
  }
}
